import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { entities, media } from "../services/api";
import {
  BoxIcon,
  LogoutIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  TrendingUpIcon,
  DollarIcon,
  ChevronRightIcon,
  ShareIcon,
  PlusIcon,
  CameraIcon,
  ClipboardListIcon,
  ShoppingBagIcon,
  ImageIcon,
  TagIcon,
  AlertIcon,
  SunIcon,
  MoonIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "../components/Icons";
import ActivityFeed from "../components/ActivityFeed";
import { toast } from "../components/Toast";
import { LOW_DATA_MODE } from "../lowData";
import { useTheme } from "../hooks/usePersistentToggle";

// Overview for one-off resale inventory: every item moves through
// acquired → priced → listed → sold, so the dashboard is organised around
// what's stuck at each step rather than stock quantities.

const DEFAULT_PROFIT_GOAL = 1000;
const MONTHS_SHOWN = 12;

const SERIES = [
  { key: "acquired", label: "Acquired", color: "var(--color-blue)" },
  { key: "listed", label: "Listed", color: "var(--color-terracotta)" },
  { key: "sold", label: "Sold", color: "var(--color-mint)" },
];

const CHANNEL_LABELS = {
  ebay: "eBay",
  offerup: "OfferUp",
  facebook: "Facebook",
  craigslist: "Craigslist",
  mercari: "Mercari",
};

const money = (v) =>
  `$${Math.round(v || 0).toLocaleString("en-US")}`;

const compactMoney = (v) =>
  Math.abs(v) >= 10000 ? `$${(v / 1000).toFixed(1)}k` : money(v);

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function readProfitGoal() {
  try {
    const v = parseFloat(localStorage.getItem("profit_goal"));
    return v > 0 ? v : DEFAULT_PROFIT_GOAL;
  } catch {
    return DEFAULT_PROFIT_GOAL;
  }
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// Change vs last month as a signed percentage, or null when there's no base
function delta(current, previous) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

function Delta({ value }) {
  if (value === null || !isFinite(value)) return null;
  const up = value >= 0;
  return (
    <span className={`dash-delta ${up ? "dash-delta-up" : "dash-delta-down"}`}>
      {up ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />}
      {Math.abs(value).toFixed(0)}%
    </span>
  );
}

function Kpi({ label, value, Icon, tone, meta, children, onClick }) {
  return (
    <button className="dash-card dash-kpi" onClick={onClick}>
      <div className="dash-kpi-head">
        <span className="dash-kpi-label">{label}</span>
        <span className={`dash-kpi-icon dash-tone-${tone}`}>
          <Icon size={18} />
        </span>
      </div>
      <div className="dash-kpi-value">
        {value}
        {children}
      </div>
      <div className="dash-kpi-meta">{meta}</div>
    </button>
  );
}

// Half-circle of radial ticks; the first `pct` of them are filled
function GoalGauge({ pct }) {
  const ticks = 56;
  const filled = Math.round(Math.min(pct, 1) * ticks);
  const cx = 120;
  const cy = 116;
  return (
    <svg viewBox="0 0 240 128" className="dash-gauge" aria-hidden="true">
      {Array.from({ length: ticks }, (_, i) => {
        const a = Math.PI - (i / (ticks - 1)) * Math.PI;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        return (
          <line
            key={i}
            x1={cx + cos * 78}
            y1={cy - sin * 78}
            x2={cx + cos * 110}
            y2={cy - sin * 110}
            className={i < filled ? "dash-gauge-on" : "dash-gauge-off"}
          />
        );
      })}
    </svg>
  );
}

function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartMode, setChartMode] = useState("count"); // count | amount
  const [hoverMonth, setHoverMonth] = useState(null);
  const [profitGoal, setProfitGoal] = useState(readProfitGoal);
  const [editingGoal, setEditingGoal] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    if (LOW_DATA_MODE) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await entities.dashboard();
      setData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
      setError("Couldn't load your dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Roll the daily activity up into the last 12 local months
  const months = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = MONTHS_SHOWN - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("en-US", { month: "short" }),
        acquired: 0,
        acquiredAmount: 0,
        listed: 0,
        listedAmount: 0,
        sold: 0,
        soldAmount: 0,
      });
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    for (const day of data?.activity || []) {
      const b = byKey.get(day.date?.slice(0, 7));
      if (!b) continue;
      for (const k of [
        "acquired",
        "acquiredAmount",
        "listed",
        "listedAmount",
        "sold",
        "soldAmount",
      ]) {
        b[k] += day[k] || 0;
      }
    }
    return buckets;
  }, [data?.activity]);

  const seriesValue = (m, key) =>
    chartMode === "amount" ? m[`${key}Amount`] : m[key];

  const chartMax = useMemo(
    () =>
      Math.max(
        1,
        ...months.flatMap((m) => SERIES.map((s) => seriesValue(m, s.key))),
      ),
    [months, chartMode], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const saveGoal = (raw) => {
    const v = parseFloat(raw);
    setEditingGoal(false);
    if (!(v > 0)) return;
    setProfitGoal(v);
    try {
      localStorage.setItem("profit_goal", String(v));
    } catch {
      // Goal just won't persist across reloads
    }
  };

  // Share this month's numbers; falls back to the clipboard on desktop
  const handleShare = async () => {
    const t = data?.sales?.thisMonth || {};
    const monthLabel = new Date().toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const text =
      `My reselling in ${monthLabel}: sold ${plural(t.count || 0, "item")} ` +
      `for ${money(t.revenue)} (${money(t.profit)} profit), ` +
      `${data?.streaks?.current || 0}-day streak.`;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Progress summary copied to clipboard");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Failed to share:", err);
        toast.error("Failed to share progress");
      }
    }
  };

  const mobileHeader = (
    <header className="header mobile-only">
      <div className="header-content" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="sidebar-logo" style={{ width: 32, height: 32 }}>
            <BoxIcon size={16} />
          </span>
          <span style={{ fontSize: "1.125rem", fontWeight: 700 }}>
            FrugalStack
          </span>
        </div>
        <div className="header-actions-right">
          <button
            onClick={toggleTheme}
            className="btn btn-ghost btn-icon"
            aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
          >
            {theme === "dark" ? <SunIcon size={20} /> : <MoonIcon size={20} />}
          </button>
          <button
            onClick={onLogout}
            className="btn btn-ghost btn-icon"
            aria-label="Log out"
          >
            <LogoutIcon size={20} />
          </button>
        </div>
      </div>
    </header>
  );

  if (LOW_DATA_MODE) {
    return (
      <div className="container" style={{ paddingTop: "3rem" }}>
        <div className="card text-center" style={{ padding: "var(--spacing-2xl)" }}>
          <p style={{ marginBottom: "var(--spacing-md)", color: "var(--text-secondary)" }}>
            Data-saver mode is on — dashboard queries are paused to conserve
            the D1 read budget. Use the sidebar to get to your inventory.
          </p>
          <button onClick={() => navigate("/products")} className="btn btn-primary">
            Go to Products
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Load failed with nothing to show — render a real error instead of a
  // dashboard full of fake zeros.
  if (error && !data) {
    return (
      <div className="container" style={{ paddingTop: "3rem" }}>
        <div className="card text-center" style={{ padding: "var(--spacing-2xl)" }}>
          <p style={{ marginBottom: "var(--spacing-md)", color: "var(--text-secondary)" }}>
            {error}
          </p>
          <button onClick={loadDashboard} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const counts = data?.counts || { products: 0, containers: 0, locations: 0 };
  const pipeline = data?.pipeline || {};
  const thisMonth = data?.sales?.thisMonth || { count: 0, revenue: 0, profit: 0 };
  const lastMonth = data?.sales?.lastMonth || { count: 0, revenue: 0, profit: 0 };
  const recentSales = data?.sales?.recent || [];
  const byParent = data?.byParent || [];
  const streaks = data?.streaks || { current: 0, longest: 0 };

  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysLeft = daysInMonth - now.getDate();
  const goalPct = profitGoal > 0 ? Math.max(thisMonth.profit, 0) / profitGoal : 0;
  const toGoal = Math.max(profitGoal - thisMonth.profit, 0);

  // The one-line to-do summary under the greeting, most actionable first
  const todo = [
    { n: pipeline.needs_info, text: "need photos or a description", to: "/products?board=organization.needs_info" },
    { n: pipeline.not_priced, text: "need a price", to: "/products?board=sales.not_priced" },
    { n: pipeline.ready, text: "are priced but not listed", to: "/products?board=sales.ready" },
  ].filter((t) => t.n > 0);

  const attention = [
    {
      n: pipeline.stale,
      title: `Listed over ${pipeline.stale_days || 60} days`,
      sub: "Consider a price drop or relist",
      Icon: AlertIcon,
      tone: "rose",
      to: "/sales/listed",
    },
    {
      n: pipeline.single_marketplace,
      title: "On only one marketplace",
      sub: "Cross-list for more eyes",
      Icon: ClipboardListIcon,
      tone: "blue",
      to: "/sales/listed",
    },
    {
      n: pipeline.not_priced,
      title: "No price yet",
      sub: "Can't list until it's priced",
      Icon: TagIcon,
      tone: "amber",
      to: "/products?board=sales.not_priced",
    },
    {
      n: pipeline.unfiled,
      title: "Not in a container",
      sub: "File it so you can find it",
      Icon: ContainerIcon,
      tone: "mint",
      to: "/board?p=organization",
    },
  ];

  // Last 14 local days for the streak strip, oldest first
  const activityByDate = new Map((data?.activity || []).map((a) => [a.date, a]));
  const recentDays = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13 + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const a = activityByDate.get(key);
    return {
      key,
      label: d.toLocaleDateString("en-US", { weekday: "narrow" }),
      total: a ? (a.acquired || 0) + (a.listed || 0) + (a.sold || 0) : 0,
    };
  });
  const recentMax = Math.max(1, ...recentDays.map((d) => d.total));

  const hovered = hoverMonth !== null ? months[hoverMonth] : null;
  const current = months[months.length - 1];

  return (
    <div className="dash-page">
      {mobileHeader}

      <div className="dash">
        {/* Greeting + KPIs */}
        <section className="dash-welcome">
          <span className="dash-date-chip">
            <span className="dash-live-dot" />
            {now.toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </span>
          <h1 className="dash-title">{greeting()}</h1>
          <p className="dash-todo">
            {todo.length === 0 ? (
              "Everything on hand is priced and listed. Time to go find more."
            ) : (
              <>
                {todo.map((t, i) => (
                  <React.Fragment key={t.to}>
                    {i > 0 && (i === todo.length - 1 ? " and " : ", ")}
                    <button className="dash-inline-link" onClick={() => navigate(t.to)}>
                      {plural(t.n, "item")}
                    </button>{" "}
                    {t.text}
                  </React.Fragment>
                ))}
                .
              </>
            )}
          </p>
          <div className="dash-actions">
            <button className="btn btn-primary" onClick={() => navigate("/create")}>
              <PlusIcon size={16} /> Add item
            </button>
            <button className="btn btn-secondary" onClick={() => navigate("/scan")}>
              <CameraIcon size={16} /> Scan
            </button>
          </div>
        </section>

        <Kpi
          label="Unsold on hand"
          value={counts.products.toLocaleString("en-US")}
          Icon={ProductIcon}
          tone="blue"
          meta={`${compactMoney(pipeline.tied_up)} tied up · ${plural(pipeline.unlisted || 0, "unlisted item")}`}
          onClick={() => navigate("/products")}
        />
        <Kpi
          label="Listed"
          value={(pipeline.listed || 0).toLocaleString("en-US")}
          Icon={ClipboardListIcon}
          tone="terracotta"
          meta={`${compactMoney(pipeline.listed_value)} asking · ${pipeline.ready || 0} ready to list`}
          onClick={() => navigate("/sales/listed")}
        />
        <Kpi
          label="Sold this month"
          value={money(thisMonth.revenue)}
          Icon={ShoppingBagIcon}
          tone="mint"
          meta={`${plural(thisMonth.count, "item")} · ${money(thisMonth.profit)} profit`}
          onClick={() => navigate("/sales/sold")}
        >
          <Delta value={delta(thisMonth.revenue, lastMonth.revenue)} />
        </Kpi>

        {/* Flow chart */}
        <section className="dash-card dash-chart-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Item flow</h2>
            <div className="dash-legend">
              {SERIES.map((s) => (
                <span key={s.key}>
                  <i style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
            <div className="dash-segmented" role="group" aria-label="Chart units">
              <button
                className={chartMode === "count" ? "active" : ""}
                onClick={() => setChartMode("count")}
              >
                Items
              </button>
              <button
                className={chartMode === "amount" ? "active" : ""}
                onClick={() => setChartMode("amount")}
              >
                Dollars
              </button>
            </div>
          </div>

          <div className="dash-chart" onMouseLeave={() => setHoverMonth(null)}>
            {months.map((m, i) => (
              <div
                key={m.key}
                className={`dash-chart-col ${i === months.length - 1 ? "is-current" : ""} ${hoverMonth === i ? "is-hover" : ""}`}
                onMouseEnter={() => setHoverMonth(i)}
              >
                <div className="dash-chart-bars">
                  {SERIES.map((s) => (
                    <span
                      key={s.key}
                      style={{
                        height: `${(seriesValue(m, s.key) / chartMax) * 100}%`,
                        background: s.color,
                      }}
                    />
                  ))}
                </div>
                <span className="dash-chart-label">{m.label}</span>
              </div>
            ))}
            {hovered && (
              <div
                className="dash-tooltip"
                style={{
                  left: `${((hoverMonth + 0.5) / months.length) * 100}%`,
                  transform: `translateX(${hoverMonth > months.length / 2 ? "-105%" : "5%"})`,
                }}
              >
                <strong>{hovered.label}</strong>
                {SERIES.map((s) => (
                  <div key={s.key}>
                    <i style={{ background: s.color }} />
                    {s.label}
                    <b>
                      {hovered[s.key]} · {money(hovered[`${s.key}Amount`])}
                    </b>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="dash-chart-foot">
            This month so far: <b>{current.acquired}</b> in,{" "}
            <b>{current.listed}</b> listed, <b>{current.sold}</b> sold
            {current.acquired > 0 && (
              <>
                {" "}
                — sell-through{" "}
                <b>{Math.round((current.sold / current.acquired) * 100)}%</b>
              </>
            )}
            .
          </p>
        </section>

        {/* Profit goal */}
        <section className="dash-card dash-goal-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Profit goal</h2>
            {editingGoal ? (
              <input
                className="dash-goal-input"
                type="number"
                min="1"
                defaultValue={profitGoal}
                autoFocus
                aria-label="Monthly profit goal"
                onBlur={(e) => saveGoal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveGoal(e.target.value);
                  if (e.key === "Escape") setEditingGoal(false);
                }}
              />
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingGoal(true)}>
                {money(profitGoal)} / mo
              </button>
            )}
          </div>
          <div className="dash-gauge-wrap">
            <GoalGauge pct={goalPct} />
            <div className="dash-gauge-center">
              <span className="dash-gauge-pct">{Math.round(goalPct * 100)}%</span>
              <span className="dash-gauge-caption">of monthly goal</span>
            </div>
          </div>
          <div className="dash-goal-stats">
            <div>
              <span>Profit</span>
              <b>{money(thisMonth.profit)}</b>
            </div>
            <div>
              <span>Last month</span>
              <b>{money(lastMonth.profit)}</b>
            </div>
          </div>
          <div className="dash-goal-note">
            <TrendingUpIcon size={14} />
            {toGoal > 0 ? (
              <span>
                <b>{money(toGoal)}</b> to go, {plural(daysLeft, "day")} left.
              </span>
            ) : (
              <span>Goal reached. Nice.</span>
            )}
          </div>
        </section>

        {/* Needs attention */}
        <section className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Needs attention</h2>
          </div>
          <div className="dash-rows">
            {attention.map((a) => (
              <button
                key={a.title}
                className={`dash-row dash-row-tint dash-tint-${a.tone}`}
                onClick={() => navigate(a.to)}
                disabled={!a.n}
              >
                <span className={`dash-row-icon dash-tone-${a.tone}`}>
                  <a.Icon size={16} />
                </span>
                <span className="dash-row-body">
                  <span className="dash-row-title">{a.title}</span>
                  <span className="dash-row-sub">{a.sub}</span>
                </span>
                <span className="dash-row-count">{a.n || 0}</span>
                <ChevronRightIcon size={16} className="dash-row-chevron" />
              </button>
            ))}
          </div>
        </section>

        {/* Recent sales */}
        <section className="dash-card">
          <div className="dash-card-head">
            <div>
              <h2 className="dash-card-title">Recent sales</h2>
              <span className="dash-card-sub">Price, profit and where it sold</span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/sales/sold")}>
              All
            </button>
          </div>
          {recentSales.length === 0 ? (
            <div className="dash-empty">No sales yet. Your first one shows up here.</div>
          ) : (
            <div className="dash-rows">
              {recentSales.map((s) => {
                const profit =
                  s.purchase_price !== null && s.purchase_price !== undefined
                    ? s.sale_price - (s.fees || 0) - s.purchase_price
                    : null;
                return (
                  <button
                    key={s.id}
                    className="dash-row"
                    onClick={() => s.entity_id && navigate(`/entity/${s.entity_id}`)}
                  >
                    <span className="dash-thumb">
                      {s.image_path ? (
                        <img src={media.getImageUrl(s.image_path)} alt="" loading="lazy" />
                      ) : (
                        <ImageIcon size={18} />
                      )}
                    </span>
                    <span className="dash-row-body">
                      <span className="dash-row-title">{s.name || "Untitled item"}</span>
                      <span className="dash-row-sub">
                        {[
                          CHANNEL_LABELS[s.channel] || s.channel,
                          new Date(s.sale_date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <span className="dash-row-end">
                      <b>{money(s.sale_price)}</b>
                      {profit !== null && (
                        <span className={profit >= 0 ? "dash-pos" : "dash-neg"}>
                          {profit >= 0 ? "+" : "−"}
                          {money(Math.abs(profit))}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section className="dash-card dash-activity-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Recent activity</h2>
          </div>
          <ActivityFeed limit={5} />
        </section>

        {/* Where it sits */}
        <section className="dash-card dash-wide">
          <div className="dash-card-head">
            <div>
              <h2 className="dash-card-title">Where it sits</h2>
              <span className="dash-card-sub">Unsold items by container</span>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/organize")}>
              Organize
            </button>
          </div>
          {byParent.length === 0 ? (
            <div className="dash-empty">
              Nothing is filed yet.{" "}
              <button className="dash-inline-link" onClick={() => navigate("/board?p=organization")}>
                File items into containers
              </button>{" "}
              to see where your value sits.
            </div>
          ) : (
            <div className="dash-rows">
              {byParent.map((p) => {
                const ParentIcon = p.entity_type === "location" ? LocationIcon : ContainerIcon;
                const fill = p.capacity > 0 ? Math.min(p.items / p.capacity, 1) : null;
                return (
                  <button key={p.id} className="dash-row dash-place" onClick={() => navigate(`/entity/${p.id}`)}>
                    <span className="dash-row-icon dash-tone-terracotta">
                      <ParentIcon size={16} />
                    </span>
                    <span className="dash-row-body">
                      <span className="dash-row-title">{p.name}</span>
                      <span className="dash-row-sub">{compactMoney(p.value)} asking</span>
                      {fill !== null && (
                        <span className="dash-bar">
                          <span
                            style={{ width: `${fill * 100}%` }}
                            className={fill >= 0.9 ? "is-full" : ""}
                          />
                        </span>
                      )}
                    </span>
                    <span className="dash-row-end">
                      <b>{p.items}</b>
                      <span>{fill !== null ? `of ${p.capacity}` : p.items === 1 ? "item" : "items"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Streak */}
        <section className="dash-card dash-streak-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Streak</h2>
            <button className="btn btn-ghost btn-sm" onClick={handleShare}>
              <ShareIcon size={14} /> Share
            </button>
          </div>
          <div className="dash-streak">
            <span className="dash-streak-num">{streaks.current}</span>
            <span className="dash-streak-unit">
              day{streaks.current === 1 ? "" : "s"} in a row
            </span>
          </div>
          <p className="dash-card-sub">
            Any day you add, list or sell counts. Longest: {plural(streaks.longest, "day")}.
          </p>
          <div className="dash-days" aria-label="Activity over the last 14 days">
            {recentDays.map((d) => (
              <span
                key={d.key}
                title={`${d.key}: ${d.total} actions`}
                className={d.total > 0 ? "is-on" : ""}
                style={{ "--lvl": d.total / recentMax }}
              >
                <i />
                <small>{d.label}</small>
              </span>
            ))}
          </div>
          <div className="dash-mini-stats">
            <button onClick={() => navigate("/containers")}>
              <ContainerIcon size={14} /> {counts.containers} containers
            </button>
            <button onClick={() => navigate("/locations")}>
              <LocationIcon size={14} /> {counts.locations} locations
            </button>
            <button onClick={() => navigate("/sales/analytics")}>
              <DollarIcon size={14} /> Analytics
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
